/**
 * Fiche de renseignements de l'école (par année scolaire) : frais d'inscription et de réinscription,
 * tranches par classe, articles, pièces du dossier et textes imprimés. Données du serveur.
 */
import { http, orUndefined } from "./http";
import { getActiveSchoolYear } from "./schoolService";
import type {
  ClassFiche,
  DossierItem,
  EnrollmentFees,
  EnrollmentPiece,
  FicheArticle,
  FicheConfig,
  FicheTranche,
  PaymentPeriod,
} from "@/types/fiche";
import type { FeeKind, SchoolClass } from "@/types";

/* -------------------------------------------------------------------------- */
/* Format du backend                                                           */
/* -------------------------------------------------------------------------- */

export interface ApiFeeItem {
  id: number;
  schoolYear: string;
  kind: FeeKind;
  label: string;
  amount: number;
  dueDate: string | null;
  position: number;
  classId: number | null;
  cycleId: number | null;
  appliesToAllClasses: boolean;
  classIds: number[];
  hasPayments: boolean;
}

interface ApiPiece {
  id: number;
  label: string;
  position: number;
  appliesToAllClasses: boolean;
  classIds: number[];
}

interface ApiFicheContent {
  header: { group: string; subtitle: string; cycles: string; contacts: string };
  periods: Array<{ label: string; value: string }>;
  notices: string[];
  closing: string;
  signature: string;
}

/** Fiche chargée : en plus de la vue, les frais bruts (identifiants des frais d'inscription). */
export interface LoadedFiche extends FicheConfig {
  items: ApiFeeItem[];
}

const yearOf = (schoolYear?: string) => schoolYear || getActiveSchoolYear();

/** Frais d'une année (par défaut l'année active). */
export function listFeeItems(schoolYear?: string): Promise<ApiFeeItem[]> {
  return http.get<ApiFeeItem[]>("/fees", { schoolYear: yearOf(schoolYear) || undefined });
}

function toPiece(p: ApiPiece): DossierItem {
  return {
    id: String(p.id),
    label: p.label,
    appliesToAllClasses: p.appliesToAllClasses,
    classIds: p.classIds.map(String),
  };
}

/**
 * Fiche complète d'une année. `classes` : toutes les classes de l'école (chacune a sa fiche, même
 * sans tranche encore saisie).
 */
export async function fetchFicheConfig(classes: SchoolClass[], schoolYear?: string): Promise<LoadedFiche> {
  const year = yearOf(schoolYear);
  const [items, pieces, content] = await Promise.all([
    listFeeItems(year),
    http.get<ApiPiece[]>("/dossier-pieces"),
    http.get<ApiFicheContent>("/fiche-settings"),
  ]);
  const dossiers = pieces.map(toPiece);
  const amountOf = (kind: FeeKind) => items.find((i) => i.kind === kind)?.amount ?? 0;

  const classFiches: ClassFiche[] = classes.map((c) => ({
    id: c.id,
    classId: c.id,
    cycleId: c.cycleId,
    tranches: items
      .filter((i) => i.kind === "TRANCHE" && String(i.classId) === c.id)
      .sort((a, b) => a.position - b.position || a.id - b.id)
      .map(
        (i): FicheTranche => ({
          id: String(i.id),
          label: i.label,
          amount: i.amount,
          dueDate: i.dueDate ?? "",
          hasPayments: i.hasPayments,
        }),
      ),
    dossierIds: dossiers.filter((d) => d.appliesToAllClasses || d.classIds.includes(c.id)).map((d) => d.id),
  }));

  return {
    header: { ...content.header, schoolYear: year },
    enrollmentFees: { inscription: amountOf("INSCRIPTION"), reinscription: amountOf("REINSCRIPTION") },
    dossiers,
    classFiches,
    articles: items
      .filter((i) => i.kind === "ARTICLE")
      .sort((a, b) => a.position - b.position || a.id - b.id)
      .map((i) => ({
        id: String(i.id),
        label: i.label,
        amount: i.amount,
        appliesToAllClasses: i.appliesToAllClasses,
        classIds: i.classIds.map(String),
        hasPayments: i.hasPayments,
      })),
    periods: content.periods.map((p, index) => ({ id: `per-${index + 1}`, label: p.label, value: p.value })),
    notices: content.notices,
    closing: content.closing,
    signature: content.signature,
    items,
  };
}

/** Nouvelle tranche (identifiant provisoire côté écran) : pas encore enregistrée. */
const isNewId = (id: string) => !/^\d+$/.test(id);

/** Fiche d'une classe : ses tranches (remplacées en bloc) et les pièces cochées pour elle. */
export async function updateClassFiche(
  classId: string,
  patch: { tranches: FicheTranche[]; dossierIds: string[] },
  schoolYear?: string,
): Promise<void> {
  await http.put(`/fees/classes/${classId}`, {
    schoolYear: yearOf(schoolYear) || undefined,
    tranches: patch.tranches.map((t) => ({
      id: isNewId(t.id) ? undefined : Number(t.id),
      label: t.label.trim(),
      amount: t.amount,
      dueDate: orUndefined(t.dueDate) ?? null,
    })),
    pieceIds: patch.dossierIds.map(Number),
  });
}

/** Frais d'inscription et de réinscription, valables pour toutes les classes de l'année. */
export async function updateEnrollmentFees(fiche: LoadedFiche, fees: EnrollmentFees): Promise<void> {
  const schoolYear = fiche.header.schoolYear;
  const save = async (kind: FeeKind, label: string, amount: number) => {
    const existing = fiche.items.find((i) => i.kind === kind);
    if (existing) {
      if (existing.amount !== amount) await http.put(`/fees/${existing.id}`, { amount });
    } else if (amount > 0) {
      await http.post("/fees", { schoolYear, kind, label, amount });
    }
  };
  await save("INSCRIPTION", "Frais d’inscription", fees.inscription);
  await save("REINSCRIPTION", "Frais de réinscription", fees.reinscription);
}

/** Articles : ajoutés, modifiés ou retirés pour correspondre à la liste saisie. */
export async function updateArticles(fiche: LoadedFiche, articles: FicheArticle[]): Promise<void> {
  const schoolYear = fiche.header.schoolYear;
  const body = (a: FicheArticle, index: number) => ({
    label: a.label.trim(),
    amount: a.amount,
    position: index + 1,
    appliesToAllClasses: a.appliesToAllClasses,
    classIds: a.appliesToAllClasses ? [] : a.classIds.map(Number),
  });
  const kept = new Set(articles.filter((a) => !isNewId(a.id)).map((a) => a.id));
  for (const removed of fiche.articles.filter((a) => !kept.has(a.id))) {
    await http.delete(`/fees/${removed.id}`);
  }
  for (const [index, article] of articles.entries()) {
    if (isNewId(article.id)) await http.post("/fees", { schoolYear, kind: "ARTICLE", ...body(article, index) });
    else await http.put(`/fees/${article.id}`, body(article, index));
  }
}

/** Textes imprimés (en-tête, périodes, avis, formule finale). */
export async function updateFicheTexts(
  fiche: FicheConfig,
  patch: Partial<Pick<FicheConfig, "periods" | "notices" | "closing" | "signature">> & {
    header?: Partial<FicheConfig["header"]>;
  },
): Promise<void> {
  const header = { ...fiche.header, ...patch.header };
  const periods: PaymentPeriod[] = patch.periods ?? fiche.periods;
  await http.put("/fiche-settings", {
    header: { group: header.group, subtitle: header.subtitle, cycles: header.cycles, contacts: header.contacts },
    periods: periods.filter((p) => p.label.trim()).map((p) => ({ label: p.label.trim(), value: p.value.trim() })),
    notices: patch.notices ?? fiche.notices,
    closing: patch.closing ?? fiche.closing,
    signature: patch.signature ?? fiche.signature,
  });
}

/** Recopie la fiche d'une année sur une année encore vide (rentrée). */
export async function copyFiche(fromYear: string, toYear: string): Promise<void> {
  await http.post("/fees/copy", { fromYear, toYear });
}

/* -------------------------------------------------------------------------- */
/* Pièces du dossier                                                           */
/* -------------------------------------------------------------------------- */

export async function addDossier(label: string, appliesToAllClasses = false): Promise<DossierItem> {
  return toPiece(await http.post<ApiPiece>("/dossier-pieces", { label: label.trim(), appliesToAllClasses }));
}

export async function updateDossier(id: string, patch: Partial<Omit<DossierItem, "id">>): Promise<DossierItem> {
  return toPiece(
    await http.put<ApiPiece>(`/dossier-pieces/${id}`, {
      label: patch.label?.trim(),
      appliesToAllClasses: patch.appliesToAllClasses,
      classIds: patch.classIds?.map(Number),
    }),
  );
}

export async function removeDossier(id: string): Promise<void> {
  await http.delete(`/dossier-pieces/${id}`);
}

/** Pièces exigées pour une inscription et ce qui a été fourni. */
export async function getEnrollmentPieces(enrollmentId: string): Promise<EnrollmentPiece[]> {
  const rows = await http.get<Array<{ pieceId: number; label: string; provided: boolean; providedAt: string | null }>>(
    `/enrollments/${enrollmentId}/pieces`,
  );
  return rows.map((r) => ({ pieceId: String(r.pieceId), label: r.label, provided: r.provided, providedAt: r.providedAt ?? "" }));
}

export async function setEnrollmentPiece(enrollmentId: string, pieceId: string, provided: boolean): Promise<EnrollmentPiece[]> {
  await http.put(`/enrollments/${enrollmentId}/pieces/${pieceId}`, { provided });
  return getEnrollmentPieces(enrollmentId);
}

/* -------------------------------------------------------------------------- */
/* Aides de lecture                                                            */
/* -------------------------------------------------------------------------- */

export function findClassFiche(cfg: FicheConfig, classId: string): ClassFiche | undefined {
  return cfg.classFiches.find((f) => f.classId === classId);
}

/** Pièces exigées pour une classe. */
export function dossiersForClass(cfg: FicheConfig, classId: string): DossierItem[] {
  return cfg.dossiers.filter((d) => d.appliesToAllClasses || d.classIds.includes(classId));
}
